import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Check, Mail, Phone } from "lucide-react";
import { Eyebrow, MaskedLine, Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { useContent } from "../lib/content";

const CHAPTER_NUMS = ["01", "02", "03"];

export default function About() {
  const { content } = useContent();
  const phoneHref = `tel:+1${content.contact.phoneDisplay.replace(/\D/g, "")}`;

  return (
    <div data-testid="about-page">
      <SEO
        title="About Jennifer & Rooted Business Advisors | Certified Payroll Professional"
        description="Meet Jennifer, a Certified Payroll Professional with nearly two decades of experience across banking, accounting, payroll, and incentive compensation."
      />
      <section className="bg-forest-deep pb-24 pt-44 text-cream">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Eyebrow light>About Rooted</Eyebrow>
          <h1 className="mt-8 font-display text-5xl font-medium leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl" data-testid="about-headline">
            <MaskedLine delay={0.15}>{content.about.line1}</MaskedLine>
            <MaskedLine delay={0.3} className="italic text-gold">
              {content.about.line2}
            </MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl leading-relaxed text-cream/75 md:text-lg">{content.about.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="meet-jennifer-section">
        <div className="grid items-center gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-gold" />
              <img
                src="/assets/jennifer.jpg"
                alt="Jennifer, Founder of Rooted Business Advisors"
                className="relative w-full object-cover shadow-[0_8px_30px_rgba(26,54,36,0.12)]"
                data-testid="jennifer-photo"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-6">
            <Reveal>
              <Eyebrow>Meet Jennifer</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
                {content.about.jenniferHeading1} <span className="italic text-olive">{content.about.jenniferHeading2}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-forest" data-testid="jennifer-credential">
                <BadgeCheck className="h-4 w-4 text-olive" strokeWidth={1.5} />
                Certified Payroll Professional
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 leading-relaxed text-sage">{content.about.bio1}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-5 leading-relaxed text-sage">{content.about.bio2}</p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={phoneHref}
                  data-testid="about-phone-link"
                  className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                >
                  <Phone className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  {content.contact.phoneDisplay}
                </a>
                <a
                  href={`mailto:${content.contact.email}`}
                  data-testid="about-email-link"
                  className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                >
                  <Mail className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  Email Jennifer
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-y border-forest/10 bg-parchment" data-testid="chapters-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <Eyebrow>The Rooted Way</Eyebrow>
          </Reveal>
          <div className="mt-14">
            {content.about.chapters.map((c, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="grid gap-6 border-t border-forest/15 py-12 last:border-b md:grid-cols-12">
                  <span className="font-display text-5xl italic text-gold md:col-span-2">{CHAPTER_NUMS[i] || String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-3xl font-medium text-forest md:col-span-4">{c.title}</h3>
                  <p className="leading-relaxed text-sage md:col-span-6">{c.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream" data-testid="core-values-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <Eyebrow>What Guides Us</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
              Our Core <span className="italic text-olive">Values.</span>
            </h2>
          </Reveal>
          <div className="mt-14">
            {content.coreValues.map((v, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="grid gap-4 border-t border-forest/15 py-9 last:border-b md:grid-cols-12">
                  <span className="font-display text-3xl italic text-gold md:col-span-2">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-2xl font-medium text-forest md:col-span-3">{v.title}</h3>
                  <p className="text-sm leading-relaxed text-sage md:col-span-7">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-forest text-cream" data-testid="who-we-serve-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow light>Is Rooted Right for You?</Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight md:text-5xl" data-testid="who-we-serve-heading">
                  {content.whoWeServe.heading}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 leading-relaxed text-cream/75">{content.whoWeServe.intro}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-6">
                {content.whoWeServe.bullets.map((b, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <li className="flex items-start gap-4 border-b border-cream/15 pb-6">
                      <Check className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                      <span className="leading-relaxed text-cream/85">{b}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
              <Reveal delay={0.2}>
                <p className="mt-10 font-display text-xl italic leading-relaxed text-gold">{content.whoWeServe.outro}</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="about-cta-section">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
              {content.cta.heading}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
            >
              Request a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
